import { useState } from 'react';
import { DELIVERY_CITIES } from './Locations';


export default function Hero({ onCityMatch }) {
  const [address, setAddress] = useState('');
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleFindFood = () => {
    const trimmed = address.trim();
    if (!trimmed) {
      showToast('warning', 'Please enter your address or use current location first.');
      return;
    }
    const lower = trimmed.toLowerCase();
    const matched = DELIVERY_CITIES.find((city) =>
      lower.includes(city.toLowerCase())
    );
    if (matched) {
      onCityMatch(matched);
      showToast('success', `Great news! We deliver in ${matched}. Check out available restaurants below.`);
    } else {
      showToast('error', "We don't deliver there yet. See all our available cities below!");
    }
    document.getElementById('locations')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocError('Geolocation is not supported by your browser.');
      return;
    }
    setLocLoading(true);
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await res.json();
          const display = data.display_name || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
          setAddress(display);
        } catch {
          setAddress(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
        } finally {
          setLocLoading(false);
        }
      },
      (err) => {
        setLocLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setLocError('Location access denied. Please allow location in your browser.');
        } else {
          setLocError('Unable to retrieve your location. Please try again.');
        }
      },
      { timeout: 10000 }
    );
  };

  const toastColors = {
    success: 'bg-[#009E81] text-white',
    warning: 'bg-[#FFCD1A] text-gray-900',
    error: 'bg-red-500 text-white',
  };

  const toastIcons = {
    success: 'ri-checkbox-circle-line',
    warning: 'ri-alert-line',
    error: 'ri-map-pin-2-line',
  };

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">

      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=vibrant%20top-down%20aerial%20flat%20lay%20of%20diverse%20delicious%20african%20food%20dishes%20bowls%20spread%20on%20a%20dark%20wooden%20table%2C%20colorful%20jollof%20rice%2C%20suya%2C%20soup%2C%20fresh%20vegetables%2C%20warm%20ambient%20lighting%2C%20rich%20colors%2C%20food%20photography%20editorial%20style%2C%20dark%20moody%20background%2C%20ultra%20high%20detail%2C%20professional%20studio%20photo&width=1600&height=900&seq=hero-bg-v3&orientation=landscape"
          alt="Delicious food spread"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-start gap-3 px-5 py-3.5 rounded-2xl shadow-lg max-w-sm w-[90vw] transition-all duration-300 ${toastColors[toast.type]}`}
          role="alert"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className={`${toastIcons[toast.type]} text-base`} />
          </div>
          <p className="text-sm font-medium leading-snug">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-auto w-5 h-5 flex items-center justify-center opacity-70 hover:opacity-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col md:flex-row items-center max-w-7xl mx-auto w-full px-4 sm:px-8 pt-28 md:pt-32 pb-24 md:pb-36 gap-12 md:gap-16">

        {/* Left — Text & CTAs */}
        <div className="flex-1 flex flex-col gap-6 items-center md:items-start text-center md:text-left">

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-award-fill text-[#FFCD1A]" />
            </div>
            #1 Food Delivery App in Africa
          </div>

          {/* Headline */}
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight">
              Crave it.{' '}
              <span className="text-[#FFCD1A]">Tap it.</span>
              <br />
              Get it delivered.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/70 max-w-md leading-relaxed">
              Order favorite meals from cloud kitchen and premium restaurant near you, earn rewards on every bite - All on the sweet plate app.
            </p>
          </div>

          {/* Address search */}
          <div className="w-full max-w-md">
            <div
              className={`bg-white rounded-2xl p-1.5 flex items-center gap-2 w-full transition-all shadow-xl ${
                locLoading ? 'ring-2 ring-[#FFCD1A]' : ''
              }`}
            >
              <div className="w-9 h-9 flex items-center justify-center text-[#009E81] flex-shrink-0">
                {locLoading ? (
                  <i className="ri-loader-4-line text-lg animate-spin" />
                ) : (
                  <i className="ri-map-pin-line text-lg" />
                )}
              </div>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleFindFood()}
                placeholder="Enter your delivery address…"
                className="flex-1 outline-none text-sm text-gray-700 bg-transparent placeholder-gray-400 min-w-0"
              />
              {address && (
                <button
                  onClick={() => setAddress('')}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line" />
                </button>
              )}
              <button
                onClick={handleFindFood}
                className="bg-[#009E81] text-white rounded-xl px-3 sm:px-5 py-2.5 text-sm font-semibold hover:bg-[#008570] active:scale-95 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
              >
                <i className="ri-arrow-right-line" />
                <span className="hidden sm:inline">Find Food</span>
              </button>
            </div>
            <button
              onClick={handleUseCurrentLocation}
              disabled={locLoading}
              className="mt-2 flex items-center gap-1.5 text-xs text-white/60 hover:text-white font-medium cursor-pointer disabled:opacity-50 transition-colors mx-auto md:mx-0"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${locLoading ? 'ri-loader-4-line animate-spin' : 'ri-navigation-line'}`} />
              </div>
              {locLoading ? 'Detecting location…' : 'Use my current location'}
            </button>
            {locError && (
              <p className="text-xs text-red-400 mt-1 max-w-xs text-center md:text-left">{locError}</p>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 w-full max-w-md">
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-white/40 text-xs font-medium">or download the app</span>
            <div className="flex-1 h-px bg-white/20" />
          </div>

          {/* App Store Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
            {/* Google Play */}
            <a
              href="#download"
              className="flex-1 flex items-center gap-3 bg-black hover:bg-gray-900 active:scale-95 transition-all rounded-xl px-4 py-3 cursor-pointer border border-white/10"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-google-play-fill text-2xl text-white" />
              </div>
              <div className="text-left">
                <p className="text-white/60 text-[10px] leading-none">GET IT ON</p>
                <p className="text-white font-bold text-sm leading-tight mt-0.5">Google Play</p>
              </div>
              <div className="ml-auto w-5 h-5 flex items-center justify-center text-white/30">
                <i className="ri-arrow-right-s-line" />
              </div>
            </a>

            {/* App Store */}
            <div
              className="flex-1 flex items-center gap-3 bg-black/60 rounded-xl px-4 py-3 border border-white/10 cursor-not-allowed opacity-70 relative"
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-apple-fill text-2xl text-white" />
              </div>
              <div className="text-left">
                <p className="text-white/60 text-[10px] leading-none">DOWNLOAD ON THE</p>
                <p className="text-white font-bold text-sm leading-tight mt-0.5">App Store</p>
              </div>
              <div className="ml-auto flex-shrink-0 bg-white/15 rounded-full px-2 py-0.5">
                <span className="text-white/80 text-[9px] font-semibold tracking-wide uppercase whitespace-nowrap">Coming Soon</span>
              </div>
            </div>
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-4 flex-wrap justify-center md:justify-start">
            <div className="flex -space-x-2">
              {[
                'https://readdy.ai/api/search-image?query=young%20african%20woman%20smiling%20face%20portrait%20close%20up%20natural%20lighting%20clean%20background&width=48&height=48&seq=av1&orientation=squarish',
                'https://readdy.ai/api/search-image?query=young%20african%20man%20smiling%20face%20portrait%20close%20up%20natural%20lighting%20clean%20background&width=48&height=48&seq=av2&orientation=squarish',
                'https://readdy.ai/api/search-image?query=african%20woman%20happy%20face%20portrait%20close%20up%20warm%20lighting%20clean%20background&width=48&height=48&seq=av3&orientation=squarish',
                'https://readdy.ai/api/search-image?query=african%20man%20cheerful%20face%20portrait%20close%20up%20natural%20light%20clean%20background&width=48&height=48&seq=av4&orientation=squarish',
              ].map((src, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white overflow-hidden flex-shrink-0">
                  <img src={src} alt="user" className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-star-fill text-[#FFCD1A] text-xs" />
                  </div>
                ))}
                <span className="text-white font-bold text-sm ml-1">4.9</span>
              </div>
              <p className="text-white/50 text-xs">Trusted by 2M+ food lovers</p>
            </div>
          </div>
        </div>

        {/* Right — Phone mockup */}
        <div className="flex-shrink-0 flex justify-center items-end relative hidden md:flex">
          <div className="relative">
            {/* Glow */}
            <div className="absolute inset-0 bg-[#009E81]/30 blur-3xl rounded-full scale-75" />
            {/* Phone frame */}
            <div className="relative w-[220px] lg:w-[260px] h-[440px] lg:h-[520px] bg-gray-900 rounded-[2.5rem] border-4 border-white/20 overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=mobile%20food%20delivery%20app%20interface%20screenshot%20on%20smartphone%20screen%20showing%20restaurant%20menu%20food%20cards%20clean%20modern%20UI%20green%20teal%20color%20scheme%20bright%20and%20clear%20app%20design%20high%20quality%20render&width=260&height=520&seq=phone-mockup-v2&orientation=portrait"
                alt="Sweetplate app"
                className="w-full h-full object-cover object-top"
              />
              {/* Status bar notch */}
              <div className="absolute top-0 left-0 right-0 h-6 bg-black/30 flex items-center justify-center">
                <div className="w-16 h-3 bg-black rounded-full" />
              </div>
            </div>

            {/* Floating order card */}
            <div className="absolute -left-16 top-16 bg-white rounded-2xl px-3.5 py-2.5 shadow-xl flex items-center gap-2.5 min-w-[148px]">
              <div className="w-9 h-9 flex items-center justify-center bg-[#FFF3DC] rounded-xl flex-shrink-0">
                <i className="ri-motorbike-fill text-[#FFCD1A] text-base" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-medium">Order on the way!</p>
                <p className="text-xs font-bold text-gray-800">12 mins away</p>
              </div>
            </div>

            {/* Floating reward card */}
            <div className="absolute -right-12 bottom-24 bg-[#009E81] rounded-2xl px-3.5 py-2.5 shadow-xl flex items-center gap-2.5 min-w-[136px]">
              <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-coin-fill text-white text-base" />
              </div>
              <div>
                <p className="text-[10px] text-white/70 font-medium">You earned</p>
                <p className="text-xs font-bold text-white">+50 SP Points</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 w-full z-10 overflow-hidden leading-none pointer-events-none">
        <div className="w-full h-16 bg-gradient-to-t from-white to-transparent" />
      </div>
    </section>
  );
}
